import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, Gem, Star, Lightbulb, GraduationCap, HeartHandshake, ArrowRight } from 'lucide-react';
import { SPONSORSHIP } from '@/constants/testIds';
import { SPONSORSHIP_TIERS } from '@/lib/aawta';

const ICONS = { Gem, Star, Lightbulb, GraduationCap, HeartHandshake };

export const Sponsorship = ({ onOpenInquiry }) => {
  return (
    <section id="sponsors" className="py-24 md:py-32 bg-aawta-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-navy opacity-30" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-aawta-magenta/15 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-aawta-magenta/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="overline text-aawta-magenta">2026 Sponsorship Opportunities</span>
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 tracking-tight leading-[1.1]">
            Partner with AAWTA. Shape the future workforce.
          </h2>
          <div className="w-14 h-[3px] bg-aawta-magenta rounded-full mt-6" />
          <p className="mt-6 text-[17px] leading-relaxed text-white/80">
            Every sponsorship level fuels workforce development, education, and economic
            mobility in our region — and comes with founding-year recognition you won’t get
            twice.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {SPONSORSHIP_TIERS.map((t, i) => {
            const Icon = ICONS[t.icon];
            const highlight = t.highlight;
            return (
              <div
                key={t.slug}
                data-testid={SPONSORSHIP.card(t.slug)}
                style={{ animationDelay: `${i * 80}ms` }}
                className={`reveal group relative rounded-2xl p-7 flex flex-col transition-all duration-300 border ${
                  highlight
                    ? 'bg-white text-aawta-navy border-white shadow-[0_20px_60px_rgba(229,0,125,0.35)] lg:-translate-y-4'
                    : 'bg-white/8 backdrop-blur-md border-white/15 text-white hover:-translate-y-1 hover:bg-white/12'
                }`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-aawta-magenta text-white text-[11px] font-bold tracking-wider uppercase shadow-aawta-magenta">
                    Most Impact
                  </div>
                )}

                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    highlight ? 'bg-aawta-magenta text-white' : 'bg-white/10 text-aawta-magenta'
                  }`}
                >
                  <Icon className="h-6 w-6" strokeWidth={2} />
                </div>

                <h3 className={`font-display font-bold text-xl mt-5 ${highlight ? '' : ''}`}>
                  {t.name}
                </h3>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="font-display font-bold text-4xl tracking-tight">{t.price}</div>
                </div>
                <div
                  className={`overline mt-1 ${
                    highlight ? 'text-aawta-magenta' : 'text-white/70'
                  }`}
                >
                  {t.availability}
                </div>
                <p
                  className={`mt-4 text-sm leading-relaxed ${
                    highlight ? 'text-aawta-muted' : 'text-white/75'
                  }`}
                >
                  {t.tagline}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13.5px] leading-relaxed">
                      <Check
                        className={`h-4 w-4 mt-0.5 shrink-0 ${
                          highlight ? 'text-aawta-magenta' : 'text-aawta-magenta'
                        }`}
                        strokeWidth={2.5}
                      />
                      <span className={highlight ? 'text-aawta-navy/90' : 'text-white/85'}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex flex-col gap-2 pt-5 border-t border-current/10">
                  <Button
                    data-testid={SPONSORSHIP.becomeSponsor(t.slug)}
                    onClick={() => onOpenInquiry('sponsor', { tier: t.name })}
                    className={`rounded-full font-medium ${
                      highlight
                        ? 'bg-aawta-magenta hover:bg-aawta-magenta-hover text-white shadow-aawta-magenta'
                        : 'bg-aawta-magenta hover:bg-aawta-magenta-hover text-white'
                    }`}
                  >
                    Become Sponsor
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={SPONSORSHIP.learnMore(t.slug)}
                    onClick={() => onOpenInquiry('sponsor', { tier: t.name, learnMore: true })}
                    variant="ghost"
                    className={`rounded-full font-medium ${
                      highlight
                        ? 'text-aawta-navy hover:bg-aawta-navy/5'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-6 p-8 rounded-3xl bg-white/8 backdrop-blur-md border border-white/15">
          <div>
            <h4 className="font-display font-bold text-2xl">Interested in vendor booths or exhibits?</h4>
            <p className="text-white/75 mt-1">
              Reserve exhibit space and connect directly with regional leaders and attendees.
            </p>
          </div>
          <Button
            data-testid="sponsors-vendor-cta"
            onClick={() => onOpenInquiry('vendor')}
            variant="outline"
            className="rounded-full bg-white text-aawta-navy hover:bg-white/90 border-white font-medium px-6"
          >
            Inquire About Vendor Booth
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Sponsorship;
